// Servicio para gestión de médicos (API Backend)
import axios from 'axios';
import config from '../config/api';

const API_BASE_URL = config.endpoints.medicos;

const medicoService = {
    /**
     * Obtener todos los médicos
     * @returns {Promise<Array>} Lista de médicos
     */
    async getAllMedicos(page = null, pageSize = null) {
        try {
            let url = `${API_BASE_URL}/`;
            const params = [];
            if (page) params.push(`page=${page}`);
            if (pageSize) params.push(`pageSize=${pageSize}`);
            if (params.length) url += `?${params.join('&')}`;
            // If pagination params provided, return full axios response so caller can read pagination metadata
            const response = await axios.get(url);
            if (page) return response; // paginated: caller expects response.data with { data, total, ... }
            // legacy behavior: return array directly
            return response.data;
        } catch (error) {
            console.error('Error al obtener médicos:', error);
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    /**
     * Obtener médico por ID
     * @param {string} id - ID del médico
     * @returns {Promise<Object>} Médico encontrado
     */
    async getMedicoById(id) {
        try {
            const response = await axios.get(`${API_BASE_URL}/id/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error al obtener médico:', error);
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    /**
     * Crear nuevo médico
     * @param {Object} medicoData - Datos del médico
     * @returns {Promise<Object>} Médico creado
     */
    async createMedico(medicoData) {
        try {
            const response = await axios.post(`${API_BASE_URL}/crear`, {
                HOSPITAL_ID: medicoData.HOSPITAL_ID,
                ESPECIALIDAD_ID: medicoData.ESPECIALIDAD_ID,
                MEDICO_NOMBRE: medicoData.MEDICO_NOMBRE,
                MEDICO_APELLIDO: medicoData.MEDICO_APELLIDO,
                MEDICO_CEDULA: medicoData.MEDICO_CEDULA,
                MEDICO_TELEFONO: medicoData.MEDICO_TELEFONO || null,
                MEDICO_DIRECCION: medicoData.MEDICO_DIRECCION || null
            });
            
            return response.data;
        } catch (error) {
            console.error('Error al crear médico:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    /**
     * Actualizar médico
     * @param {string} id - ID del médico
     * @param {Object} medicoData - Datos actualizados
     * @returns {Promise<Object>} Médico actualizado
     */
    async updateMedico(id, medicoData) {
        try {
            const response = await axios.put(`${API_BASE_URL}/actualizar/${id}`, {
                HOSPITAL_ID: medicoData.HOSPITAL_ID,
                ESPECIALIDAD_ID: medicoData.ESPECIALIDAD_ID,
                MEDICO_NOMBRE: medicoData.MEDICO_NOMBRE,
                MEDICO_APELLIDO: medicoData.MEDICO_APELLIDO,
                MEDICO_CEDULA: medicoData.MEDICO_CEDULA,
                MEDICO_TELEFONO: medicoData.MEDICO_TELEFONO,
                MEDICO_DIRECCION: medicoData.MEDICO_DIRECCION
            });
            
            return response.data;
        } catch (error) {
            console.error('Error al actualizar médico:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    /**
     * Eliminar médico (desactivar)
     * @param {string} id - ID del médico
     * @returns {Promise<Object>} Resultado
     */
    async deleteMedico(id) {
        try {
            const response = await axios.delete(`${API_BASE_URL}/eliminar/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error al eliminar médico:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    /**
     * Activar médico
     * @param {string} id - ID del médico
     * @returns {Promise<Object>} Resultado
     */
    async activarMedico(id) {
        try {
            const response = await axios.patch(`${API_BASE_URL}/activar/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error al activar médico:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    /**
     * Obtener médicos por hospital
     * @param {string} hospitalId - ID del hospital
     * @returns {Promise<Array>} Lista de médicos
     */
    async getMedicosByHospital(hospitalId) {
        try {
            const response = await axios.get(`${API_BASE_URL}/hospital/${hospitalId}`);
            return response.data;
        } catch (error) {
            console.error('Error al obtener médicos por hospital:', error);
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    /**
     * Obtener médicos por especialidad
     * @param {string} especialidadId - ID de la especialidad
     * @returns {Promise<Array>} Lista de médicos
     */
    async getMedicosByEspecialidad(especialidadId) {
        try {
            const response = await axios.get(`${API_BASE_URL}/especialidad/${especialidadId}`);
            return response.data;
        } catch (error) {
            console.error('Error al obtener médicos por especialidad:', error);
            throw new Error(`Error HTTP: ${error.response?.status || 'Desconocido'}`);
        }
    },

    // Métodos de compatibilidad
    async getMedicosDisponibles() {
        try {
            const medicos = await this.getAllMedicos();
            // Filtrar solo médicos activos si existe el campo de estado
            return medicos.filter(m => !m.MEDICO_ESTADO || m.MEDICO_ESTADO === 'Activo');
        } catch (error) {
            console.error('Error al obtener médicos disponibles:', error);
            return [];
        }
    }
};

export default medicoService;
